/**
 * Formats a number as Mongolian currency (MNT)
 * @param amount - The number to format
 * @param options - Formatting options
 * @returns Formatted currency string
 */
export const formatMNT = (
  amount: number | string,
  options: {
    showSymbol?: boolean;
    showDecimals?: boolean;
    decimalPlaces?: number;
  } = {}
): string => {
  const {
    showSymbol = true,
    showDecimals = false,
    decimalPlaces = 0,
  } = options;

  // Convert string to number if needed
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;

  // Format the number with thousand separators
  const formattedNumber = new Intl.NumberFormat('mn-MN', {
    minimumFractionDigits: showDecimals ? decimalPlaces : 0,
    maximumFractionDigits: showDecimals ? decimalPlaces : 0,
  }).format(numAmount);

  // Add currency symbol if needed
  return showSymbol ? `${formattedNumber}₮` : formattedNumber;
};

export const toFormat = (dateInput?: string | Date | number | null): string => {
  if (!dateInput) return '';

  let date: Date;

  if (dateInput instanceof Date) {
    date = dateInput;
  } else if (typeof dateInput === 'number') {
    // Сөрөг тоо байвал буцаах
    if (dateInput < 0) {
      console.warn('Invalid negative timestamp:', dateInput);
      return '';
    }

    // Unix timestamp секунд эсвэл миллисекунд эсэхийг шалгах
    const timestamp = dateInput > 1000000000000 ? dateInput : dateInput * 1000;
    date = new Date(timestamp);
  } else if (typeof dateInput === 'string') {
    // ISO date string format
    date = new Date(dateInput);
  } else {
    return '';
  }

  if (isNaN(date.getTime()) || date.getFullYear() < 1900) {
    console.warn('Invalid date:', dateInput);
    return '';
  }

  // YYYY-MM-DD format буцаах
  return date.toISOString().split('T')[0];
};

export const calculateDuration = (
  startDate: Date | string,
  endDate: Date | string,
  currentLanguage: number
) => {
  if (!startDate) return '';

  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date();

  const years = end.getFullYear() - start.getFullYear();
  const months = end.getMonth() - start.getMonth();

  let totalMonths = years * 12 + months;
  if (totalMonths < 0) totalMonths = 0;

  const finalYears = Math.floor(totalMonths / 12);
  const finalMonths = totalMonths % 12;

  const parts: string[] = [];
  if (finalYears > 0)
    parts.push(
      `${finalYears} ${currentLanguage === 1 ? 'жил' : finalYears > 1 ? 'years' : 'year'}`
    );
  if (finalMonths > 0)
    parts.push(
      `${finalMonths} ${currentLanguage === 1 ? 'сар' : finalMonths > 1 ? 'months' : 'month'}`
    );
  return parts.join(', ');
};

export const displaySalary = (param: {
  timeType?: number;
  salary?: string;
  timeSalary?: number;
  changeable?: boolean;
  salaryId?: number;
  usdRate?: number;
}): string => {
  const { timeType, salary, timeSalary, changeable, usdRate, salaryId } = param;
  const result: string[] = [];
  if (salaryId && salaryId === 500) {
    return 'тохиролцоно';
  }
  if (salary && salary.trim() !== '') {
    result.push(salary.trim());
    if (changeable) {
      result.push('тохиролцоно');
    }
    return result.join(' / ');
  }

  const parseAmount = (value?: string): number | null => {
    if (!value) return null;
    const normalized = value.replace(/,/g, '').trim();
    const amount = Number.parseInt(normalized, 10);
    return Number.isFinite(amount) ? amount : null;
  };

  const formatAmount = (amount: number): string => {
    if (usdRate && usdRate > 0) {
      return convertMntToUsd(amount, usdRate);
    }
    return formatMNT(amount);
  };

  if (timeType && [2, 7].includes(timeType)) {
    const amount = timeSalary ?? 0;
    if (amount) {
      const text = 'цагийн';
      result.push(`${formatAmount(amount)}`);
      result.push(`${text}`);
    }
  } else {
    if (salary) {
      const parts = salary.split('-').map((part) => part.trim());
      if (parts.length === 2) {
        const min = parseAmount(parts[0]);
        const max = parseAmount(parts[1]);
        if (min && max) {
          result.push(`${formatAmount(min)}-${formatAmount(max)}`);
        }
      } else {
        const singleAmount = parseAmount(salary);

        if (singleAmount) {
          result.push(formatAmount(singleAmount));
        }
      }
    }
  }

  if (changeable) {
    result.push('тохиролцоно');
  }

  return result.join(' / ');
};

export const formatFileSize = (bytes: number) => {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return '0 Bytes';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  const size = bytes / Math.pow(1024, i);
  return `${size.toFixed(1)} ${sizes[i]}`;
};

function convertMntToUsd(amountMnt: number, usdRate: number): string {
  const result = amountMnt / usdRate;
  return `$${result.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function NumberToComma(amount: number): string {
  return new Intl.NumberFormat('en-US').format(amount);
}

/**
 * Formats a number to Mongolian "сая" (millions) format
 * @param amount - The number to format (in MNT)
 * @returns Formatted string like "3.8 сая"
 */
export function formatToSay(amount: number): string {
  const millions = amount / 1000000;
  // Round to 1 decimal place
  const rounded = Math.round(millions * 10) / 10;
  if (rounded >= 10) {
    return `${rounded}+ сая`;
  }
  return `${rounded} сая`;
}
